import * as React from "react"
import {useMemo} from "react"
import {mapper} from "../utils/helpers";
import {CrossSellCarouselModel} from "../models/view/crossSellCarouselModel";
import {useProductVariables} from "./useProductVariables";
import {useSuspenseQuery} from "@apollo/client";
import {contentService} from "../api/contentService";
import {CollectionProductType, CrossSellType, ImageType} from "../definitions/types";

const defaultResponse = {
    name: undefined,
    models: undefined,
    carouselModel: undefined
};

export const useCollectionCarouselQuery = () => {
    const {variables: {productId, currencyCode}} = useProductVariables();

    const {data} = useSuspenseQuery(contentService.product.collection, {
        variables: {
            productId,
            currencyCode
        }
    });

    const collection = useMemo(() => {
        const {collectionName, collectionProducts} = data?.product ?? {
            collectionName: undefined,
            collectionProducts: undefined
        }

        if (!collectionName || !collectionProducts) return null;

        // Filter out products that are not cross-sellable
        // 1. Filter out products that are the current product
        // 2. Filter out products that have no price
        // 3. Filter out products that have no images or have null entries in the images array
        // 4. Map products to a cross-sell type
        const products = collectionProducts
            ? collectionProducts.products
                .filter((collectionProduct): collectionProduct is CollectionProductType => collectionProduct !== null)
                .filter((product) => Number(product.id) !== productId)
                .filter((product) => product.price)
                .filter((product) => product.media.images.dot4b.filter((image): image is ImageType => image !== null).length > 0)
                .map((product) => {
                    // Get the first image from dotFourB images
                    const {
                        url: src,
                        alt,
                        title
                    } = product.media.images.dot4b.filter((image): image is ImageType => image !== null)[0];

                    // Get price information
                    const {
                        unitPrice,
                        salePrice,
                        saleAmount,
                        salePercentage,
                        price,
                        onSale
                    } = product.price!;

                    // Map product to a cross-sell type
                    const crossSell: CrossSellType = {
                        ...product,
                        productId: product.id,
                        designerId: product.designerId,
                        path: product.variantPath,
                        customizeIt: product.isDynamicDimension || !!product.designerId,
                        minQuantity: product.minimumQuantity,
                        imageSrc: src,
                        imageAlt: alt,
                        imageTitle: title,
                        dimensionalPrice: product.isDynamicDimension ? 0 : undefined,
                        unitPrice: unitPrice,
                        salePrice: salePrice,
                        saleAmount: saleAmount,
                        salePercentage: salePercentage,
                        price: price,
                        onSale: onSale,
                        ratingStatistics: product.primaryRatings?.stats ?? product.ratings?.stats ?? null
                    };

                    return crossSell;
                })
            : [] as CrossSellType[];

        // If there are no products, return null instead of an empty array
        if (products.length === 0) return null;

        return {
            name: collectionName,
            products: products
        };
    }, [data]);

    const carouselModel = useMemo(() => {
        if (!collection?.name)
            return new CrossSellCarouselModel((<div className="fs-3 ms-2 p-3"> More in the collection </div>))

        return new CrossSellCarouselModel((
            <div className="fs-3 ms-2 p-3">
                More in the <i>{collection.name}</i>
                {!collection.name.toLowerCase().includes("collection") && " collection"}
            </div>
        ));
    }, [collection]);

    if (!collection) return defaultResponse;

    return {
        name: collection.name,
        models: collection.products.map((crossSell) => mapper.crossSellToModel(crossSell)),
        carouselModel
    };
}